import { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'

import { useAppDispatch } from 'common/hooks/redux'
import { useTimezone } from 'common/hooks/useTimezone'
import { DateService } from 'common/services/dateService'
import { debugPusher, useMultipleChannels } from 'features/Schedule/pusher/MultiplePusherProvider'

import Popup from 'common/components/Popup'
import { PUSHER_EVENTS } from 'features/Home/constants/infoConstants'
import {
  applyPatientDelete,
  applyPatientStatusUpdate,
  applyPatientUpsert,
  applyRoomStatusUpdate,
  type ScheduleCacheContext,
} from 'features/Schedule/api/optimistics.updates'
import { isOwnScheduleOrigin } from 'features/Schedule/pusher/scheduleClientId'
import {
  cancelQueuedScheduleResync,
  classifyPendingPatientUpsert,
  setScheduleResyncHandler,
} from 'features/Schedule/pusher/schedulePusherEcho'
import type {
  MassUpdateEvent,
  PatientDeleteEvent,
  PatientStatusUpdateEvent,
  PatientUpdateEvent,
  RoomStatusUpdateEvent,
} from 'features/Schedule/pusher/type'
import { SCHEDULE_TAGS, scheduleApi } from '../api/schedule.api'

const { getCurrentTimeInTimezone } = DateService

export const useSchedulePusher = (date: string) => {
  const { timeZone } = useTimezone()
  const { channels, bookingIds } = useMultipleChannels()
  const navigate = useNavigate()
  const { siteId } = useParams()
  const scheduleState = useSelector((state: any) => state.schedule)
  const scheduleQueriesRef = useRef(scheduleState?.queries)

  const dispatch = useAppDispatch()

  useEffect(() => {
    scheduleQueriesRef.current = scheduleState?.queries
  }, [scheduleState?.queries])

  useEffect(() => {
    setScheduleResyncHandler(() => {
      dispatch(
        scheduleApi.util.invalidateTags([SCHEDULE_TAGS.SCHEDULE, SCHEDULE_TAGS.SCHEDULE_WEEK]),
      )
    })
    return () => {
      setScheduleResyncHandler(null)
      cancelQueuedScheduleResync()
    }
  }, [dispatch])

  const getCacheContext = useCallback((): ScheduleCacheContext | null => {
    if (!date || !siteId) return null
    return {
      siteId,
      viewDate: date,
      scheduleQueries: scheduleQueriesRef.current || {},
    }
  }, [date, siteId])

  const massUpdate = useCallback(
    (event: MassUpdateEvent) => {
      if (debugPusher) console.log('MASS_UPDATE event received:', event)
      const { day, shouldUpdate } = event
      if (day === date && shouldUpdate) {
        Popup.showInfo(
          {
            title: 'Schedule updated',
            message: 'You have a mass update. Would you like to refresh the page?',
            mainButtonText: 'Refresh',
            secondButtonText: 'No',
          },
          () => navigate(0),
        )
      }
    },
    [date, navigate],
  )

  const patientUpsert = useCallback(
    (event: PatientUpdateEvent) => {
      const { shouldUpdate, origin_client_id, data } = event
      if (!shouldUpdate || !data) return
      if (isOwnScheduleOrigin(origin_client_id) === true) return

      const pendingPayload = classifyPendingPatientUpsert(data)
      const isTodayView = date === getCurrentTimeInTimezone(timeZone).format('YYYY-MM-DD')
      const isTodayEvent = data.date === date
      const allowTodayEmrSyncStructural = isTodayView && isTodayEvent

      const context = getCacheContext()
      if (context) {
        applyPatientUpsert(dispatch, context, data, {
          allowStructural: pendingPayload === 'none',
          allowEmrSyncStructural: allowTodayEmrSyncStructural,
        })
      }
    },
    [date, dispatch, getCacheContext, timeZone],
  )

  const patientUpdate = useCallback(
    (event: PatientUpdateEvent) => {
      if (debugPusher) console.log('PATIENT_UPDATED event received:', event)
      patientUpsert(event)
    },
    [patientUpsert],
  )

  const patientCreate = useCallback(
    (event: PatientUpdateEvent) => {
      if (debugPusher) console.log('PATIENT_CREATE event received:', event)
      patientUpsert(event)
    },
    [patientUpsert],
  )

  const patientDelete = useCallback(
    (event: PatientDeleteEvent) => {
      if (debugPusher) console.log('PATIENT_DELETE event received:', event)
      const { appointment_id, shouldUpdate, origin_client_id } = event
      if (!shouldUpdate) return

      if (!appointment_id) return
      if (isOwnScheduleOrigin(origin_client_id) === true) return

      const context = getCacheContext()
      if (context) {
        applyPatientDelete(dispatch, context, Number(appointment_id))
      }
    },
    [dispatch, getCacheContext],
  )

  const patientStatusUpdate = useCallback(
    (event: PatientStatusUpdateEvent) => {
      if (debugPusher) console.log('PATIENT_STATUS_UPDATE event received:', event)
      const { shouldUpdate, origin_client_id, appointment_id, status_code, temperature } = event
      if (!shouldUpdate) return
      if (isOwnScheduleOrigin(origin_client_id) === true) return

      const context = getCacheContext()
      if (context) {
        applyPatientStatusUpdate(dispatch, context, appointment_id, status_code, temperature)
      }
    },
    [dispatch, getCacheContext],
  )

  const roomStatusUpdate = useCallback(
    (event: RoomStatusUpdateEvent) => {
      if (debugPusher) console.log('ROOM_STATUS_UPDATE event received:', event)
      const { shouldUpdate, origin_client_id, room_id, room_status_code } = event
      if (!shouldUpdate) return
      if (isOwnScheduleOrigin(origin_client_id) === true) return

      const context = getCacheContext()
      if (context) {
        applyRoomStatusUpdate(dispatch, context, room_id, room_status_code)
      }
    },
    [dispatch, getCacheContext],
  )

  useEffect(() => {
    if (!channels.size || !bookingIds.length) {
      if (debugPusher)
        console.log('No channels or bookingIds available for event binding', {
          channelsSize: channels.size,
          bookingIdsLength: bookingIds.length,
        })
      return
    }

    if (debugPusher) console.log('Binding events to channels, count:', channels.size)

    channels.forEach((channel, channelName) => {
      if (debugPusher) console.log(`Binding events to channel: ${channelName}`)

      channel.bind(PUSHER_EVENTS.MASS_UPDATE, massUpdate)
      channel.bind(PUSHER_EVENTS.PATIENT_UPDATED, patientUpdate)
      channel.bind(PUSHER_EVENTS.PATIENT_CREATE, patientCreate)
      channel.bind(PUSHER_EVENTS.PATIENT_DELETE, patientDelete)
      channel.bind(PUSHER_EVENTS.PATIENT_STATUS_UPDATE, patientStatusUpdate)
      channel.bind(PUSHER_EVENTS.ROOM_STATUS_UPDATE, roomStatusUpdate)
    })

    return () => {
      if (debugPusher) console.log('Unbinding events from channels')
      channels.forEach((channel, channelName) => {
        if (debugPusher) console.log(`Unbinding events from channel: ${channelName}`)

        channel.unbind(PUSHER_EVENTS.MASS_UPDATE, massUpdate)
        channel.unbind(PUSHER_EVENTS.PATIENT_UPDATED, patientUpdate)
        channel.unbind(PUSHER_EVENTS.PATIENT_CREATE, patientCreate)
        channel.unbind(PUSHER_EVENTS.PATIENT_DELETE, patientDelete)
        channel.unbind(PUSHER_EVENTS.PATIENT_STATUS_UPDATE, patientStatusUpdate)
        channel.unbind(PUSHER_EVENTS.ROOM_STATUS_UPDATE, roomStatusUpdate)
      })
    }
  }, [
    channels,
    bookingIds.length,
    massUpdate,
    patientUpdate,
    patientCreate,
    patientDelete,
    patientStatusUpdate,
    roomStatusUpdate,
  ])
}
